import { useState } from 'react'
import {
  Button,
  Checkbox,
  Flex,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  ModalBody,
  ModalProps,
  Spinner,
  Text,
} from '@ritualco/jutsu'
import { useGetWorkspaceMembersMapped } from 'api/endpoints/workspaceMembers'
import { HeraModal } from 'components/HeraModal'
import { useRowsPerPage, Pagination } from 'components/Pagination'
import { IncentivePeopleTable } from 'components/Tables/IncentivePeopleTable'
import { useContent } from 'lib/content/react'
import { IncentiveRecipients } from 'types/entities'
import { IncentiveRecipientsOnSubmit } from 'views/Incentive/types'

export type IncentiveEmployeesModalProps = {
  organizationId: string
  workspaceId: string
  recipients?: IncentiveRecipients
  onSubmit: IncentiveRecipientsOnSubmit
} & Omit<ModalProps, 'children'>

export const IncentiveEmployeesModal = ({
  organizationId,
  workspaceId,
  recipients,
  onSubmit,
  onClose,
  ...modalProps
}: IncentiveEmployeesModalProps) => {
  const { formatMessage } = useContent()
  const [allEmployees, setAllEmployees] = useState<boolean>(
    recipients?.allPeople || true
  )
  const [employees, setEmployees] = useState<string[]>(recipients?.people || [])
  const [rowsPerPage, setRowsPerPage] = useRowsPerPage()
  const [pageToken, setPageToken] = useState({})
  const { status, data } = useGetWorkspaceMembersMapped({
    organizationId,
    workspaceId,
    limit: rowsPerPage,
    ...pageToken,
  })

  const noEmployees = !data?.results?.length

  return (
    <HeraModal size="4xl" onClose={onClose} {...modalProps}>
      <ModalHeader>
        {formatMessage({ id: 'pmportal.incentives.whoCanAccess' })}
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {status === 'loading' ? (
          <Spinner />
        ) : (
          <>
            <Flex justifyContent="space-between" pb={2}>
              <Checkbox
                isChecked={allEmployees}
                isDisabled={noEmployees}
                onChange={() => {
                  setAllEmployees(prevValue => !prevValue)
                }}
              >
                {formatMessage({ id: 'pmportal.incentives.allEmployees' })}
              </Checkbox>
              <Button
                variant="ghost"
                isDisabled={allEmployees || noEmployees}
                onClick={() => {
                  setEmployees([])
                }}
              >
                {formatMessage({ id: 'pmportal.global.actionRemoveAll' })}
              </Button>
            </Flex>
            <IncentivePeopleTable
              workspaceMembers={data?.results}
              people={employees}
              placeholder={formatMessage({
                id: 'pmportal.incentives.noEmployeesAvailable',
              })}
              isDisabled={allEmployees}
              onAdd={value => {
                setEmployees(prevEmployees => [...prevEmployees, value])
              }}
              onRemove={value => {
                setEmployees(prevEmployees =>
                  prevEmployees.filter(id => id !== value)
                )
              }}
            />
            {!noEmployees && (
              <Pagination
                isDisabled={allEmployees}
                hasNext={data?.hasNextPage}
                hasPrev={data?.hasPreviousPage}
                onNext={() => {
                  setPageToken({ nextPageToken: data?.paginationToken })
                }}
                onPrev={() => {
                  setPageToken({ previousPageToken: data?.paginationToken })
                }}
                onRowsPerPageChange={e =>
                  setRowsPerPage(Number(e.target.value))
                }
                rowsPerPage={rowsPerPage}
                py={3}
              />
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter justifyContent="space-between">
        <Text fontSize="small" color="primary.normal">
          {allEmployees
            ? formatMessage({ id: 'pmportal.incentives.allEmployeesAdded' })
            : formatMessage(
                { id: 'pmportal.incentives.employeesAdded' },
                { count: employees.length }
              )}
        </Text>
        <Button
          isDisabled={noEmployees}
          onClick={() => {
            onSubmit({ allPeople: allEmployees, people: employees })
            onClose()
          }}
        >
          {formatMessage({ id: 'pmportal.global.actionSave' })}
        </Button>
      </ModalFooter>
    </HeraModal>
  )
}
